import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Plus, Edit2, Trash2, GripVertical, AlertTriangle } from 'lucide-react';

export const KanbanBoard = ({ 
  kanbanData, pipelineStages, overdueLeads, openLeadDetails, 
  handleMoveLeadStage, setStageDialog, setNewStage, handleDeleteStage, isAdmin, formatDate
}) => {
  const [draggedLead, setDraggedLead] = useState(null);
  const overdueIds = new Set(overdueLeads.map(l => l.id));

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, stageSlug) => {
    e.preventDefault();
    if (draggedLead && draggedLead.status !== stageSlug) {
      handleMoveLeadStage(draggedLead.id, stageSlug);
    }
    setDraggedLead(null);
  };

  if (!kanbanData) {
    return <Skeleton className="h-96" />;
  }

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex justify-end">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              setNewStage({ name: '', color: '#3b82f6', is_win_stage: false, is_lost_stage: false });
              setStageDialog({ open: true, stage: null, isEdit: false });
            }}
            data-testid="add-stage-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Stage
          </Button>
        </div>
      )}
      
      <div className="flex gap-4 overflow-x-auto pb-4">
        {pipelineStages.map(stage => {
          const stageLeads = kanbanData.kanban[stage.slug]?.leads || [];
          
          return (
            <div 
              key={stage.slug}
              className="flex-shrink-0 w-72"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.slug)}
            >
              <div 
                className="flex items-center justify-between p-3 rounded-t-lg"
                style={{ backgroundColor: stage.color + '20' }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                  <span className="font-semibold">{stage.name}</span>
                  <Badge variant="secondary" className="text-xs">{stageLeads.length}</Badge>
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6"
                      onClick={() => {
                        setNewStage({ name: stage.name, color: stage.color, is_win_stage: stage.is_win_stage, is_lost_stage: stage.is_lost_stage });
                        setStageDialog({ open: true, stage, isEdit: true });
                      }}
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6 text-red-500"
                      onClick={() => handleDeleteStage(stage.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="bg-slate-100 rounded-b-lg p-2 min-h-[400px] space-y-2">
                {stageLeads.map(lead => {
                  const isOverdue = overdueIds.has(lead.id) || lead.is_overdue;
                  const overdueLead = overdueLeads.find(o => o.id === lead.id);
                  
                  return (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead)}
                      onClick={() => openLeadDetails(lead)}
                      className={`bg-white p-3 rounded-lg shadow-sm cursor-pointer hover:shadow-md transition-shadow ${isOverdue ? 'border-2 border-red-300' : 'border border-transparent'}`}
                      data-testid={`kanban-lead-${lead.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{lead.name}</p>
                          <p className="text-xs text-muted-foreground">{lead.phone}</p>
                        </div>
                        <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                      </div>
                      {isOverdue && (
                        <Badge variant="destructive" className="text-xs mt-2">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          {overdueLead?.days_overdue || lead.days_since_contact || 0}d Overdue
                        </Badge>
                      )}
                      {lead.source && (
                        <p className="text-xs text-muted-foreground mt-1">{lead.source}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
